// src/pages/pengamatan/destrak/hooks/useDestrakData.js
import { useState, useEffect } from "react";
import { database } from "../../../../firebase";
import { ref, onValue, off, update } from "firebase/database";

// Fungsi untuk memproses data bibit
const processBibitData = (bibit) => {
    if (!bibit || typeof bibit !== 'string' || bibit.length < 7) {
        return {
            varietas: '-',
            kelas: '-',
            jenis: '-'
        };
    }

    // Varietas: karakter ke 3-5 (index 2-4)
    const varietas = bibit.substring(2, 5);
    
    // Kelas: karakter ke 7 (index 6)
    const kelas = bibit.charAt(6);
    
    // Jenis: karakter ke 6 (index 5)
    let jenis = bibit.charAt(5);
    if (jenis === 'S') {
        jenis = 'SC';
    } else if (jenis === 'C') {
        jenis = 'CR';
    }

    return { varietas, kelas, jenis };
};

// Fungsi untuk mengubah format tanggal dari yyyy-mm-dd ke dd-mm-yyyy
const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString || dateString === '-') return '-';
    
    try {
        const [year, month, day] = dateString.split('-');
        if (year && month && day) {
            return `${day}-${month}-${year}`;
        }
        return dateString; // Return as-is if format doesn't match
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString; // Return original if there's an error
    }
};

const useDestrakData = (user, userProfile) => {
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [userJabatan, setUserJabatan] = useState(null);
    const [userRoleNames, setUserRoleNames] = useState([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Fungsi untuk mengupdate status pengamatan per plot
const updateStatusPengamatan = async (selectedItems, newStatus, onSuccess) => {
    try {
        const updates = {};
        const updatedItems = []; // Simpan item yang diupdate
        
        selectedItems.forEach(itemId => {
            const [destrakKey, plotKey] = itemId.split('-');
            console.log('Updating:', destrakKey, plotKey, 'to:', newStatus);
            
            const path = `db_freshpine/db_pengamatan/destrak/${destrakKey}/plot/${plotKey}/status_pengamatan_per_plot`;
            updates[path] = newStatus;
            updatedItems.push(itemId);
        });

        console.log('Batch updates:', updates);
        
        // Update UI langsung terlebih dahulu (optimistic update)
        setOrders(prevOrders => 
            prevOrders.map(order => 
                updatedItems.includes(order.id) 
                    ? { ...order, status_pengamatan: newStatus }
                    : order
            )
        );
        
        await update(ref(database), updates);
        
        // Panggil callback jika berhasil
        if (onSuccess) {
            onSuccess();
        }
        
        return true;
    } catch (error) {
        console.error("Error updating status:", error);
        
        // Rollback UI update jika gagal
        setRefreshTrigger(prev => prev + 1);
        
        return false;
    }
};

    useEffect(() => {
        if (!user || !userProfile) {
            setLoadingOrders(true);
            return;
        }

        console.log('User UID:', user.uid); // Debug
        console.log('User Profile:', userProfile); // Debug

        // Ambil data jabatan user dan role
        const userRef = ref(database, `db_user/tb_user/${user.uid}`);
        
        const unsubscribeUser = onValue(userRef, (userSnapshot) => {
            const userData = userSnapshot.val();
            console.log('User Data from Firebase:', userData); // Debug
            
            if (userData) {
                // Ambil data jabatan
                if (userData.id_jabatan) {
                    console.log('ID Jabatan:', userData.id_jabatan); // Debug
                    
                    const jabatanRef = ref(database, `db_config/db_wfms/tb_jabatan/${userData.id_jabatan}`);
                    
                    onValue(jabatanRef, (jabatanSnapshot) => {
                        const jabatanData = jabatanSnapshot.val();
                        console.log('Jabatan Data:', jabatanData); // Debug
                        
                        if (jabatanData) {
                            console.log('Nama Jabatan:', jabatanData.nama_jabatan); // Debug
                            setUserJabatan(jabatanData.nama_jabatan);
                        } else {
                            console.log('Jabatan data not found for ID:', userData.id_jabatan);
                        }
                    }, (error) => {
                        console.error('Error fetching jabatan data:', error);
                    });
                }

                // Ambil data role
                if (userData.id_role) {
                    console.log('ID Role:', userData.id_role); // Debug
                    
                    // Split id_role jika ada multiple roles
                    const roleIds = userData.id_role.split(';');
                    
                    // Ambil nama role untuk setiap id_role
                    const rolePromises = roleIds.map(roleId => {
                        const roleRef = ref(database, `db_config/db_wfms/tb_role/${roleId}`);
                        return new Promise((resolve) => {
                            onValue(roleRef, (roleSnapshot) => {
                                const roleData = roleSnapshot.val();
                                resolve(roleData ? roleData.nama_role : null);
                            }, { onlyOnce: true });
                        });
                    });
                    
                    Promise.all(rolePromises).then(roleNames => {
                        const filteredRoleNames = roleNames.filter(name => name !== null);
                        console.log('User Role Names:', filteredRoleNames); // Debug
                        setUserRoleNames(filteredRoleNames);
                    });
                }
            } else {
                console.log('User data not found');
            }
        }, (error) => {
            console.error('Error fetching user data:', error);
        });

        const destrakRef = ref(database, 'db_freshpine/db_pengamatan/destrak');
        const lokasiRef = ref(database, 'db_freshpine/tb_lokasi');
        
        const unsubscribeDestrak = onValue(destrakRef, (snapshot) => {
            const destrakData = [];
            
            // Ambil data lokasi terlebih dahulu
            onValue(lokasiRef, (lokasiSnapshot) => {
                const lokasiData = {};
                
                // Buat mapping id-lokasi ke data lokasi
                lokasiSnapshot.forEach((childSnapshot) => {
                    const lokasi = childSnapshot.val();
                    if (lokasi.id_lokasi) {
                        lokasiData[lokasi.id_lokasi] = lokasi;
                    }
                });
                
                // Process destrak data
                snapshot.forEach((childSnapshot) => {
                    const parentData = childSnapshot.val();
                    const destrakKey = childSnapshot.key; // Simpan key destrak
                    
                    // Iterasi melalui setiap plot
                    if (parentData.plot) {
                        Object.keys(parentData.plot).forEach(plotKey => {
                            const plotData = parentData.plot[plotKey];
                            
                            // Hanya ambil data dengan status_pengamatan_per_plot = "Approved" atau "Processed"
                            if (plotData.status_pengamatan_per_plot === "Approved" || plotData.status_pengamatan_per_plot === "Processed") {
                                // Cari data lokasi berdasarkan id_lokasi
                                const lokasiInfo = parentData.id_lokasi ? lokasiData[parentData.id_lokasi] : null;
                                
                                // Proses data bibit jika ada
                                const bibitProcessed = lokasiInfo && lokasiInfo.bibit 
                                    ? processBibitData(lokasiInfo.bibit)
                                    : { varietas: '-', kelas: '-', jenis: '-' };
                                
                                // Format tanggal awal_rawat
                                const awalRawatFormatted = lokasiInfo && lokasiInfo.mulai_rawat 
                                    ? formatDateToDDMMYYYY(lokasiInfo.mulai_rawat)
                                    : '-';
                                
                                // Gunakan destrakKey dan plotKey untuk membuat ID yang unik
                                destrakData.push({
                                    id: `${destrakKey}-${plotKey}`, // Pastikan format ini konsisten
                                    destrakKey: destrakKey, // Simpan juga secara terpisah untuk debugging
                                    plotKey: plotKey, // Simpan juga secara terpisah untuk debugging
                                    status_pengamatan: plotData.status_pengamatan_per_plot || '-',
                                    tanggal_pengamatan: parentData.tanggal_pengamatan || '-',
                                    tanggal_forcing: '-',
                                    umur: plotData.hsf || '-',
                                    lokasi: parentData.lokasi || '-',
                                    plot: plotData.plot || '-',
                                    status: lokasiInfo ? lokasiInfo.status || '-' : '-',
                                    varietas: bibitProcessed.varietas,
                                    kelas: bibitProcessed.kelas,
                                    jenis: bibitProcessed.jenis,
                                    awal_rawat: awalRawatFormatted,
                                    no_sample: plotData.nomor_sample || '-',
                                    
                                    // Data dari data_entry.destrak_tanaman
                                    size: plotData.data_entry?.destrak_tanaman?.size_dt || '-',
                                    berat_tanaman: plotData.data_entry?.destrak_tanaman?.berat_tanaman_dt || '-',
                                    berat_buah_crown: plotData.data_entry?.destrak_tanaman?.berat_buah_dan_crown_dt || '-',
                                    berat_buah: plotData.data_entry?.destrak_tanaman?.berat_buah_dt || '-',
                                    berat_crown: plotData.data_entry?.destrak_tanaman?.berat_crown_dt || '-',
                                    diameter_buah: plotData.data_entry?.destrak_tanaman?.diameter_buah_dt || '-',
                                    panjang_buah: plotData.data_entry?.destrak_tanaman?.panjang_buah_dt || '-',
                                    panjang_crown: plotData.data_entry?.destrak_tanaman?.panjang_crown_dt || '-',
                                    panjang_pedangkel: plotData.data_entry?.destrak_tanaman?.panjang_pedangkel_dt || '-',
                                    berat_pedangkel: plotData.data_entry?.destrak_tanaman?.berat_pedangkel_dt || '-',
                                    panjang_d_leaf: plotData.data_entry?.destrak_tanaman?.panjang_d_leaf_dt || '-',
                                    lebar_d_leaf: plotData.data_entry?.destrak_tanaman?.lebar_d_leaf_dt || '-',
                                    berat_d_leaf: plotData.data_entry?.destrak_tanaman?.berat_d_leaf_dt || '-',
                                    jumlah_d_leaf: plotData.data_entry?.destrak_tanaman?.jumlah_d_leaf_dt || '-',
                                    
                                    // Data dari data_entry.jumlah_mata
                                    kanan: plotData.data_entry?.jumlah_mata?.kanan_jm_dt || '-',
                                    kiri: plotData.data_entry?.jumlah_mata?.kiri_jm_dt || '-',
                                    atas: plotData.data_entry?.jumlah_mata?.atas_jm_dt || '-',
                                    
                                    size_buah: plotData.data_entry?.destrak_tanaman?.size_buah_dt || '-'
                                });
                            }
                        });
                    }
                });

                // 🔥 PERBAIKAN: Sorting data yang benar
                const sortedData = destrakData.sort((a, b) => {
                    // 1. Urutkan berdasarkan lokasi (ascending)
                    const lokasiCompare = a.lokasi.localeCompare(b.lokasi);
                    if (lokasiCompare !== 0) return lokasiCompare;
                    
                    // 2. Jika lokasi sama, urutkan berdasarkan plot (ascending)
                    const plotCompare = a.plot.localeCompare(b.plot);
                    if (plotCompare !== 0) return plotCompare;
                    
                    // 3. Jika lokasi dan plot sama, urutkan berdasarkan no_sample (numerik ascending)
                    // Hilangkan leading zeros dan konversi ke number
                    const noSampleA = parseInt(a.no_sample) || 0;
                    const noSampleB = parseInt(b.no_sample) || 0;
                    
                    return noSampleA - noSampleB;
                });
                
                //const reversedData = sortedData.reverse();
                //setOrders(reversedData);
                //setLoadingOrders(false);

                setOrders(sortedData); // 🔥 HAPUS .reverse() jika tidak diperlukan
                setLoadingOrders(false);
                
                // Debug: Tampilkan struktur data
                //console.log('Destrak data loaded:', reversedData);
                console.log('Destrak data loaded and sorted:', sortedData);
            }, (error) => {
                console.error("Error fetching lokasi data:", error);
                setLoadingOrders(false);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data lokasi",
                    severity: "error"
                });
            });
        }, (error) => {
            console.error("Error fetching destrak data:", error);
            setLoadingOrders(false);
            setSnackbar({
                open: true,
                message: "Gagal memuat data destrak",
                severity: "error"
            });
        });
        
        return () => {
            off(userRef);
            off(destrakRef, 'value');
            off(lokasiRef, 'value');
        };
    }, [user, userProfile, refreshTrigger]);

    return { orders,
        loadingOrders,
        snackbar,
        setSnackbar,
        userJabatan,
        userRoleNames,
        updateStatusPengamatan };
};

export default useDestrakData;